import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { buildHelperText } from 'src/utils/components';
import RadioButtonGroup from 'src/components/RadioButtonGroup';
import messages from 'src/pages/ProjectDetailsPage/components/JobForm/components/JobEditPanel/messages';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const noMarginSx = { margin: 0, marginTop: -0.5 };

interface BooleanFieldProps {
  error?: boolean;
  helperText?: string;
  field: UCM.DynamicFieldType;
  value: { value?: boolean | null } | undefined | string;
  onChange: (field: UCM.DynamicFieldType, { value }: { value: string }) => void;
}

export default function BooleanField({
  value,
  field,
  onChange,
  helperText = '',
  error,
  ...others
}: BooleanFieldProps) {
  const { formatMessage } = useIntl();

  const booleanOptions = useMemo(
    () => [
      { value: 'true', label: formatMessage(messages.yesLabel) },
      { value: 'false', label: formatMessage(messages.noLabel) },
    ],
    [],
  );

  const handleChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
      onChange(field, { value });
    },
    [field, onChange],
  );

  const parsedValue = useMemo(() => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    return (value.value ?? '').toString();
  }, [value]);

  return (
    <FormControl fullWidth error={!!error}>
      <RadioButtonGroup
        {...others}
        label={field.label}
        onChange={handleChange}
        options={booleanOptions}
        value={parsedValue}
      />
      <FormHelperText sx={noMarginSx}>
        {buildHelperText(field.description, helperText)}
      </FormHelperText>
    </FormControl>
  );
}
